import { useMutation } from "@tanstack/react-query";
import { useAuthContext } from "BEDROCK/auth/auth-provider";

export type ShippingAddress = {
  id: string;
  user_id: string;
  line1: string;
  line2?: string;
  city: string;
  postcode: string;
  country: "GB";
  is_default: boolean;
  created_at: string;
  updated_at: string;
};

export function useAddressRemoteStore() {
  const { client, userID } = useAuthContext();

  const fetchUserAddresses = async (): Promise<ShippingAddress[]> => {
    if (!userID || !client)
      throw new Error("useAddressRemoteStore: User not authenticated");

    const { data, error } = await client
      .from("shipping_addresses")
      .select("*")
      .eq("user_id", userID)
      .order("is_default", { ascending: false })
      .order("created_at", { ascending: false });

    if (error) throw error;
    return data as ShippingAddress[];
  };

  const fetchDefaultAddress = async (): Promise<ShippingAddress | null> => {
    if (!userID || !client)
      throw new Error("useAddressRemoteStore: User not authenticated");

    const { data, error } = await client
      .from("shipping_addresses")
      .select("*")
      .eq("user_id", userID)
      .eq("is_default", true)
      .single();

    if (error) {
      if (error.code === "PGRST116") {
        // No rows returned
        return null;
      }
      throw error;
    }
    return data as ShippingAddress;
  };

  const save = useMutation({
    mutationFn: async (address: Omit<ShippingAddress, "created_at" | "updated_at">) => {
      if (!userID || !client)
        throw new Error("useAddressRemoteStore: User not authenticated");

      // If this is being set as default, unset all other defaults first
      if (address.is_default) {
        await client
          .from("shipping_addresses")
          .update({ is_default: false })
          .eq("user_id", userID);
      }

      const { data, error } = await client
        .from("shipping_addresses")
        .upsert(
          [
            {
              id: address.id,
              user_id: userID,
              line1: address.line1,
              line2: address.line2 || null,
              city: address.city,
              postcode: address.postcode,
              country: address.country,
              is_default: address.is_default,
            },
          ],
          { onConflict: "id" }
        )
        .select()
        .single();

      if (error) throw error;
      return data as ShippingAddress;
    },
  });

  const remove = useMutation({
    mutationFn: async (addressID: string) => {
      if (!userID || !client)
        throw new Error("useAddressRemoteStore: User not authenticated");

      const { data, error } = await client
        .from("shipping_addresses")
        .delete()
        .eq("id", addressID)
        .eq("user_id", userID);

      if (error) throw error;
      return data;
    },
  });

  return {
    fetchUserAddresses,
    fetchDefaultAddress,
    save,
    remove,
  };
}
